import React from "react";
import {
  Card,
  CardContent,
  Box,
  Stack,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
} from "@mui/material";
import { MessageCircle, Send } from "react-feather";
import ReactMarkdown from "react-markdown";
import { SectionTitle } from "./UiBits";

export default function AiAdvisorChat({
  theme,
  cardSx,
  contentSx,
  chatMessages,
  userInput,
  setUserInput,
  isLoading,
  error,
  setError,
  send,
  chatEndRef,
}) {
  const handleKeyPress = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      send();
    }
  };

  return (
    <Card sx={cardSx}>
      <CardContent
        sx={{
          ...contentSx,
          display: "flex",
          flexDirection: "column",
          minHeight: 470,
          maxHeight: 470,
        }}
      >
        <SectionTitle icon={<MessageCircle size={18} />} title="AI Advisor" />

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2.5,
            p: 2,
            bgcolor: theme.palette.mode === "background.paper",
          }}
        >
          <Stack spacing={1.5}>
            {chatMessages.map((message, index) => {
              const isUser = message.role === "user";
              return (
                <Stack
                  key={index}
                  direction="row"
                  spacing={1.25}
                  justifyContent={isUser ? "flex-end" : "flex-start"}
                  alignItems="flex-start"
                >
                  {!isUser && (
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        bgcolor: "primary.main",
                        display: "grid",
                        placeItems: "center",
                        color: "white",
                        fontSize: 10,
                        fontWeight: 900,
                        flexShrink: 0,
                        mt: 0.25,
                      }}
                    >
                      AI
                    </Box>
                  )}

                  <Box
                    sx={{
                      px: 2,
                      py: 1.25,
                      borderRadius: 2.5,
                      maxWidth: "78%",
                      bgcolor: isUser
                        ? "primary.main"
                        : theme.palette.mode === "dark"
                        ? "grey.800"
                        : "grey.100",
                      color: isUser ? "primary.contrastText" : "text.primary",
                      boxShadow:
                        theme.palette.mode === "dark"
                          ? "0 1px 2px rgba(0,0,0,0.2)"
                          : "0 1px 0 rgba(0,0,0,0.03)",
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: 13.5,
                        lineHeight: 1.45,
                        "& p": { margin: 0, marginBottom: 1 },
                        "& p:last-child": { marginBottom: 0 },
                        "& ul, & ol": {
                          margin: 0,
                          marginTop: 0.5,
                          marginBottom: 0.5,
                          paddingLeft: 2.5,
                        },
                        "& li": { marginBottom: 0.5 },
                        "& strong": { fontWeight: 700 },
                        "& h1, & h2, & h3, & h4, & h5, & h6": {
                          margin: 0,
                          marginTop: 1,
                          marginBottom: 0.5,
                          fontWeight: 700,
                          fontSize: "1em",
                        },
                        "& code": {
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.1)"
                              : "rgba(0,0,0,0.05)",
                          padding: "2px 4px",
                          borderRadius: "3px",
                          fontSize: "0.9em",
                        },
                      }}
                    >
                      <ReactMarkdown>{message.text}</ReactMarkdown>
                    </Box>
                  </Box>

                  {isUser && (
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        bgcolor: theme.palette.mode === "dark" ? "grey.700" : "grey.400",
                        display: "grid",
                        placeItems: "center",
                        color: "white",
                        fontSize: 10,
                        fontWeight: 900,
                        flexShrink: 0,
                        mt: 0.25,
                      }}
                    >
                      U
                    </Box>
                  )}
                </Stack>
              );
            })}

            {isLoading && (
              <Stack direction="row" spacing={1.25} justifyContent="flex-start" alignItems="flex-start">
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    bgcolor: "primary.main",
                    display: "grid",
                    placeItems: "center",
                    color: "white",
                    fontSize: 10,
                    fontWeight: 900,
                    flexShrink: 0,
                    mt: 0.25,
                  }}
                >
                  AI
                </Box>
                <Box
                  sx={{
                    px: 2,
                    py: 1.25,
                    borderRadius: 2.5,
                    maxWidth: "78%",
                    bgcolor: theme.palette.mode === "dark" ? "grey.800" : "grey.100",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13.5 }}>
                    Sto pensando...
                  </Typography>
                </Box>
              </Stack>
            )}

            <div ref={chatEndRef} />
          </Stack>
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mt: 1.5 }}>
            {error}
          </Alert>
        )}

        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mt: 2 }}>
          <TextField
            fullWidth
            size="medium"
            placeholder="Chiedi all'AI..."
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isLoading}
            sx={{
              "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 14 },
            }}
          />
          <IconButton
            onClick={send}
            disabled={userInput.trim() === "" || isLoading}
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              bgcolor: "primary.main",
              color: "white",
              "&:hover": { bgcolor: "primary.dark" },
              "&.Mui-disabled": { bgcolor: "grey.300", color: "grey.500" },
            }}
          >
            <Send size={18} />
          </IconButton>
        </Stack>
      </CardContent>
    </Card>
  );
}